import { JSX, useContext, useEffect, useState } from "react";
import { isEqual } from "lodash";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
  EffectivenessEfficiencyValueEnum,
  LocationConfidenceFilter,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum,
  TopProcedure,
  CareJourneyScoresValueEnum,
  TopCondition
} from "@app/projects/servicing-provider";
import { GainsPercentEnum } from "@app/projects/servicing-provider/types";
import { LocationConfidenceScoresLabelEnum } from "@app/projects/enumerators";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import TopProcedureFilter from "@app/projects/servicing-provider/provider-claims/TopProcedureFilter";
import TopConditionFilter from "@app/projects/servicing-provider/provider-claims/TopConditionFilter";
import SpecialtyPredictionModelFilter from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelFilter";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { TableType } from "@app/viewable-fields/viewableFields";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ClaimsActivityLineOfBusinessFilter from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityLineOfBusinessFilter";
import CareJourneyScoresFilter from "@app/projects/servicing-provider/provider-claims/CareJourneyScoresFilter";
import EffectivenessEfficiencyFilter from "@app/projects/servicing-provider/provider-claims/EffectivenessEfficiencyFilter";
import GainsPercentFilter from "@app/projects/servicing-provider/provider-claims/GainsPercentFilter";
import MotiveAppropriatenessFilter from "@app/projects/servicing-provider/provider-claims/MotiveAppropriatenessFilter";
import NewPatientClaimsFilter from "@app/projects/servicing-provider/provider-claims/NewPatientClaimsFilter";
import VolumeLabelFilter from "@app/projects/servicing-provider/provider-claims/VolumeLabelFilter";
import ConfidenceTierFilter from "@app/projects/servicing-provider/provider-claims/ConfidenceTierFilter";
import ProviderSearchRow from "@app/projects/servicing-provider/provider-claims/ProviderSearchRow";
import { compareConfidenceTierSelections } from "@app/shared/confidence-tier/confidenceTierLookup";

interface ProviderClaimsFiltersProps {
  clearFilters?: boolean;
  conditions?: TopCondition[];
  topConditions?: DropdownItem<TopCondition>[];
  handleProviderSearch: (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaims?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedTopConditions?: string[],
    selectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ) => void;
  lastSearchValue: string;
  lastSelectedConfidenceTierValues?: number[];
  lastSelectedCareJourneyCostValues: CareJourneyScoresValueEnum[];
  lastSelectedCareJourneyQualityValues: CareJourneyScoresValueEnum[];
  lastSelectedConditionValues: string[];
  lastSelectedEffectivenessValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedEfficiencyValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedGainsPercent?: GainsPercentEnum;
  lastSelectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
  lastSelectedVolumeLabels: ClaimsActivityVolumeLabelEnum[];
  lastSelectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[];
  lastSelectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[];
  lastSelectedNewPatientClaimsValues?: NewPatientClaimsEnum[];
  lastSelectedProcedureValues?: string[];
  topProcedures?: DropdownItem<TopProcedure>[];
  showGainsPercent?: boolean;
  tableType: TableType;
  updateChangeTracker: () => void;
  validLoadStatus: boolean;
  showLocationConfidence?: boolean;
  showCareJourneyScores?: boolean;
  showSpecialtyPredictionModel?: boolean;
  lastSelectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum;
  showConfidenceTier?: boolean;
}

const ProviderClaimsFilters = (props: ProviderClaimsFiltersProps): JSX.Element => {
  const [disabledGainsFilter, setDisabledGainsFilter] = useState<boolean>(false);
  const [gainsPercent, setGainsPercent] = useState<GainsPercentEnum>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedVolumeLabels, setSelectedVolumeLabels] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum | null>(ClaimsActivityLineOfBusinessTypeEnum.Total);
  const [selectedLocationConfidenceValues, setSelectedLocationConfidenceValues] = useState<DropdownItem<LocationConfidenceScoresLabelEnum>[]>([]);
  const [selectedMotiveAppropriatenessValues, setSelectedMotiveAppropriatenessValues] = useState<DropdownItem<MotiveScoreValueEnum>[]>([]);
  const [showMotiveAppropriateness, setShowMotiveAppropriateness] = useState<boolean>(false);
  const [selectedNewPatientClaimsValues, setSelectedNewPatientClaimsValues] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);
  const [selectedProcedureValues, setSelectedProcedureValues] = useState<DropdownItem<TopProcedure>[]>([]);
  const [selectedCareJourneyCostValues, setSelectedCareJourneyCostValues] = useState<DropdownItem<CareJourneyScoresValueEnum>[]>([]);
  const [selectedCareJourneyQualityValues, setSelectedCareJourneyQualityValues] = useState<DropdownItem<CareJourneyScoresValueEnum>[]>([]);
  const [selectedConditionValues, setSelectedConditionValues] = useState<DropdownItem<TopCondition>[]>([]);
  const [selectedSpecialtyPredictionModelFilter, setSelectedSpecialtyPredictionModelFilter] = useState<SpecialtyPredictionModelEnum>(SpecialtyPredictionModelEnum.All);
  const [selectedConfidenceTierValues, setSelectedConfidenceTierValues] = useState<DropdownItem<number>[]>([]);
  const featureFlagContext = useContext(FeatureFlagContext);
  const countyContext = useContext(CountyProjectContext);
  const zipContext = useContext(ZipProjectContext);
  // zip is first because it has defaults that we're okay with countyContext overwriting if it exists
  // in a zip project countyContext is null so it doesn't matter
  const projectContext = { ...zipContext.state, ...countyContext, };

  useEffect(() => {
    if (!showMotiveAppropriateness &&
      projectContext.project.showMotiveAppropriatenessScore &&
      projectContext.runConfiguration.showMotiveAppropriatenessScore
    ) {
      setShowMotiveAppropriateness(true);
    }
  }, [featureFlagContext?.enabledFeatureFlags, projectContext?.project, projectContext?.runConfiguration]);

  useEffect(() => handleRunConfigurationChange(), [projectContext.runConfiguration.isProjected, projectContext.runConfiguration.isProjectedTermed]);

  const mappedVolumeLabels: DropdownItem<ClaimsActivityVolumeLabelEnum>[] = Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => {
    return { key: x, name: x }
  });

  const mappedEffectivenessAndEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[] = Object.values(EffectivenessEfficiencyValueEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedMotiveScoreValues: DropdownItem<MotiveScoreValueEnum>[] = Object.values(MotiveScoreValueEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedNewPatientClaimsValues: DropdownItem<NewPatientClaimsEnum>[] = Object.values(NewPatientClaimsEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedCareJourneyCostAndQualityValues: DropdownItem<CareJourneyScoresValueEnum>[] = Object.values(CareJourneyScoresValueEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedLocationConfidenceValues: DropdownItem<LocationConfidenceScoresLabelEnum>[] = Object.values(LocationConfidenceScoresLabelEnum).map(x => {
    let name = x.toString();
    if (x === LocationConfidenceScoresLabelEnum.VeryLow)
      name = "Very Low";
    if (x === LocationConfidenceScoresLabelEnum.Unknown)
      name = "No Score Available";
    return { key: x, name }
  });

  useEffect(() => {
    if (props.showGainsPercent) {
      setGainsPercent(GainsPercentEnum.All);
    }
  }, []);

  useEffect(() => {
    if (props.clearFilters) {
      onClearFiltersClick(false);
      props.updateChangeTracker();
    }
  }, [props.clearFilters]);

  const applyDisabled = (): boolean => {
    const volumeLabelsEqual = isEqual(
      props.lastSelectedVolumeLabels?.sort((a, b) => a.localeCompare(b)),
      selectedVolumeLabels.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const effectivenessValuesEqual = isEqual(
      props.lastSelectedEffectivenessValues?.sort((a, b) => a.localeCompare(b)),
      selectedEffectivenessValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const efficiencyValuesEqual = isEqual(
      props.lastSelectedEfficiencyValues?.sort((a, b) => a.localeCompare(b)),
      selectedEfficiencyValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const locationConfidenceValuesEqual = isEqual(
      props.lastSelectedLocationConfidenceValues?.sort((a, b) => a.localeCompare(b)),
      selectedLocationConfidenceValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const motiveAppropriatenessValuesEqual = isEqual(
      props.lastSelectedMotiveAppropriatenessValues?.sort((a, b) => a.localeCompare(b)),
      selectedMotiveAppropriatenessValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const newPatientClaimsValuesEqual = isEqual(
      props.lastSelectedNewPatientClaimsValues?.sort((a, b) => a.localeCompare(b)),
      selectedNewPatientClaimsValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const procedureValuesEqual = isEqual(
      props.lastSelectedProcedureValues?.sort((a, b) => a.localeCompare(b)),
      selectedProcedureValues.map(x => x.key.code).sort((a, b) => a.localeCompare(b))
    );

    const topConditionValuesEqual = isEqual(
      props.lastSelectedConditionValues?.sort((a, b) => a.localeCompare(b)),
      selectedConditionValues.map(x => x.key.code).sort((a, b) => a.localeCompare(b))
    );

    const careJourneyCostScoreValuesEqual = isEqual(
      props.lastSelectedCareJourneyCostValues?.sort((a, b) => a.localeCompare(b)),
      selectedCareJourneyCostValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const careJourneyQualityScoreValuesEqual = isEqual(
      props.lastSelectedCareJourneyQualityValues?.sort((a, b) => a.localeCompare(b)),
      selectedCareJourneyQualityValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const confidenceTierValuesEqual = isEqual(
      [...(props.lastSelectedConfidenceTierValues ?? [])].sort(compareConfidenceTierSelections),
      selectedConfidenceTierValues.map(x => x.key).sort(compareConfidenceTierSelections)
    );

    if (props.validLoadStatus &&
      (props.showGainsPercent && !!gainsPercent || !props.showGainsPercent) &&
      selectedVolumeLabels.length > 0 &&
      selectedEffectivenessValues.length > 0 &&
      selectedEfficiencyValues.length > 0 &&
      (selectedNewPatientClaimsValues.length > 0) &&
      ((props.showCareJourneyScores && selectedCareJourneyCostValues.length > 0) || !props.showCareJourneyScores) &&
      ((props.showCareJourneyScores && selectedCareJourneyQualityValues.length > 0) || !props.showCareJourneyScores) &&
      ((props.showLocationConfidence && selectedLocationConfidenceValues.length > 0) || !props.showLocationConfidence) &&
      ((showMotiveAppropriateness && selectedMotiveAppropriatenessValues.length > 0) || !showMotiveAppropriateness) &&
      !!selectedLineOfBusiness && (
        !isEqual(props.lastSelectedGainsPercent, gainsPercent) ||
        !isEqual(props.lastSearchValue, searchValue) ||
        !volumeLabelsEqual ||
        !effectivenessValuesEqual ||
        !efficiencyValuesEqual ||
        !isEqual(props.lastSelectedLineOfBusiness, selectedLineOfBusiness) ||
        (props.showLocationConfidence && !locationConfidenceValuesEqual) ||
        (showMotiveAppropriateness && !motiveAppropriatenessValuesEqual) ||
        (!newPatientClaimsValuesEqual) ||
        !procedureValuesEqual ||
        (props.showCareJourneyScores && !careJourneyCostScoreValuesEqual) ||
        (props.showCareJourneyScores && !careJourneyQualityScoreValuesEqual) ||
        !topConditionValuesEqual ||
        (props.showSpecialtyPredictionModel && !isEqual(props.lastSelectedSpecialtyPredictionModelFilter, selectedSpecialtyPredictionModelFilter)) ||
        (props.showConfidenceTier && !confidenceTierValuesEqual)
      )
    ) {
      return false;
    }

    return true;
  }

  const onClearFiltersClick = (clearConfidenceTier = true): void => {
    if (clearConfidenceTier) {
      setSelectedConfidenceTierValues([]);
    }

    if (props.showGainsPercent) {
      setGainsPercent(GainsPercentEnum.All);
    }

    setSearchValue("");
    setSelectedEffectivenessValues(mappedEffectivenessAndEfficiencyValues);
    setSelectedEfficiencyValues(mappedEffectivenessAndEfficiencyValues);
    setSelectedVolumeLabels(mappedVolumeLabels);
    setSelectedLineOfBusiness(ClaimsActivityLineOfBusinessTypeEnum.Total);
    setSelectedNewPatientClaimsValues(mappedNewPatientClaimsValues);
    setSelectedProcedureValues([]);
    setSelectedConditionValues([]);

    if (props.showLocationConfidence) {
      setSelectedLocationConfidenceValues(mappedLocationConfidenceValues);
    }

    if (showMotiveAppropriateness) {
      setSelectedMotiveAppropriatenessValues(mappedMotiveScoreValues);
    }

    if (props.showCareJourneyScores) {
      setSelectedCareJourneyCostValues(mappedCareJourneyCostAndQualityValues);
      setSelectedCareJourneyQualityValues(mappedCareJourneyCostAndQualityValues);
    }

    if (props.showSpecialtyPredictionModel) {
      setSelectedSpecialtyPredictionModelFilter(SpecialtyPredictionModelEnum.All);
    }
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    if (props.validLoadStatus && (
      (props.showGainsPercent && gainsPercent !== GainsPercentEnum.All) ||
      searchValue !== "" ||
      selectedEffectivenessValues.length !== mappedEffectivenessAndEfficiencyValues.length ||
      selectedEfficiencyValues.length !== mappedEffectivenessAndEfficiencyValues.length ||
      selectedVolumeLabels.length !== mappedVolumeLabels.length ||
      selectedLineOfBusiness !== ClaimsActivityLineOfBusinessTypeEnum.Total ||
      (props.showLocationConfidence && selectedLocationConfidenceValues.length !== mappedLocationConfidenceValues.length) ||
      (showMotiveAppropriateness && selectedMotiveAppropriatenessValues.length !== mappedEffectivenessAndEfficiencyValues.length) ||
      (selectedNewPatientClaimsValues.length !== mappedNewPatientClaimsValues.length) ||
      (selectedProcedureValues?.length !== 0) ||
      (selectedCareJourneyCostValues.length !== mappedCareJourneyCostAndQualityValues.length && props.showCareJourneyScores) ||
      (selectedCareJourneyQualityValues.length !== mappedCareJourneyCostAndQualityValues.length && props.showCareJourneyScores) ||
      (selectedConditionValues?.length !== 0) ||
      (props.showSpecialtyPredictionModel && selectedSpecialtyPredictionModelFilter !== SpecialtyPredictionModelEnum.All) ||
      (props.showConfidenceTier && selectedConfidenceTierValues.length !== 0)
    )) {
      return false;
    }

    return true;
  }

  function handleRunConfigurationChange() {
    if (projectContext.runConfiguration.isProjected || projectContext.runConfiguration.isProjectedTermed) {
      setDisabledGainsFilter(true);
    }
    else {
      setDisabledGainsFilter(false);
    }
  }

  const codeSearchColumnClass = props.showConfidenceTier ? "col-md-2" : "col-md-3";

  return (
    <div className="row">
      <div className="col-12">
        <div className="card card-static">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <label className="label-m mb-2">
                  Filters
                </label>
              </div>
            </div>

            { /* Line of Business Section */}
            <div className="row">
              <div className="col-12">
                <label className="label-s mb-2">
                  Activity by Line of Business
                </label>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-3">
                <ClaimsActivityLineOfBusinessFilter
                  selectedLineOfBusiness={selectedLineOfBusiness}
                  setSelectedLineOfBusiness={setSelectedLineOfBusiness}
                  testId={`provider-claims-filter__select--line-of-business-${props.tableType}`}
                  validationTooltipContent="Line of business that should be considered when identifying provider claims status"
                  project={projectContext.project}
                />
              </div>
              <div className="col-md-3">
                <VolumeLabelFilter
                  mappedVolumeLabels={mappedVolumeLabels}
                  selectedVolumeLabels={selectedVolumeLabels}
                  setSelectedVolumeLabels={setSelectedVolumeLabels}
                  testId={`provider-claims-filters__multiselect--volume-label-${props.tableType}`}
                />
              </div>
              <div className="col-md-3">
                <NewPatientClaimsFilter
                  mappedValues={mappedNewPatientClaimsValues}
                  selectedValues={selectedNewPatientClaimsValues}
                  setSelectedValues={setSelectedNewPatientClaimsValues}
                  testId={`provider-claims-filters__multiselect--new-patient-claims-${props.tableType}`}
                  title="New Patient Claims"
                />
              </div>
            </div>


            { /* Performance & Evaluation Section */ }
            <div className="row">
              <div className="col-12">
                <label className="label-s mb-2">
                  Performance &amp; Evaluation
                </label>
              </div>
            </div>
            <div className="row mb-3">
              {props.showGainsPercent && (
                <div className="col-md-2">
                  <TooltipWrapper text={disabledGainsFilter ? "The Gains Percent filter is unavailable for projected and projected-termed timeslices." : ""}>
                    <GainsPercentFilter
                      gainsPercent={gainsPercent}
                      setGainsPercent={setGainsPercent}
                      testId={`provider-claims-filters__select--gains-percent-${props.tableType}`}
                      disabledGainsFilter={disabledGainsFilter}
                    >
                    </GainsPercentFilter>
                  </TooltipWrapper>
                </div>
              )}
              {props.showLocationConfidence && (
                <div className="col-md-2">
                  <LocationConfidenceFilter
                    mappedValues={mappedLocationConfidenceValues}
                    selectedValues={selectedLocationConfidenceValues}
                    setSelectedValues={setSelectedLocationConfidenceValues}
                    testId={`provider-claims-filters__multiselect--location-confidence-${props.tableType}`}
                    title="Location Confidence"
                  />
                </div>
              )}
              <div className="col-md-2 mb-2">
                <EffectivenessEfficiencyFilter
                  mappedValues={mappedEffectivenessAndEfficiencyValues}
                  selectedValues={selectedEffectivenessValues}
                  setSelectedValues={setSelectedEffectivenessValues}
                  testId={`provider-claims-filters__multiselect--effectiveness-${props.tableType}`}
                  title="Effectiveness"
                />
              </div>
              <div className="col-md-2">
                <EffectivenessEfficiencyFilter
                  mappedValues={mappedEffectivenessAndEfficiencyValues}
                  selectedValues={selectedEfficiencyValues}
                  setSelectedValues={setSelectedEfficiencyValues}
                  testId={`provider-claims-filters__multiselect--efficiency-${props.tableType}`}
                  title="Efficiency"
                />
              </div>
              {showMotiveAppropriateness && (
                <div className="col-md-2">
                  <MotiveAppropriatenessFilter
                    mappedValues={mappedMotiveScoreValues}
                    selectedValues={selectedMotiveAppropriatenessValues}
                    setSelectedValues={setSelectedMotiveAppropriatenessValues}
                    testId={`provider-claims-filters__multiselect--motive-appropriateness-${props.tableType}`}
                  />
                </div>
              )}
              {(props.showCareJourneyScores) && (
                <div className="col-md-2">
                  <CareJourneyScoresFilter
                    careJourneyScoreValues={mappedCareJourneyCostAndQualityValues}
                    selectedValues={selectedCareJourneyCostValues}
                    setSelectedValues={setSelectedCareJourneyCostValues}
                    testId={`provider-claims-filters__multiselect--carejourney-scores-${props.tableType}`}
                    title="CareJourney Cost"
                  />
                </div>
              )}
              {(props.showCareJourneyScores) && (
                <div className="col-md-2">
                  <CareJourneyScoresFilter
                    careJourneyScoreValues={mappedCareJourneyCostAndQualityValues}
                    selectedValues={selectedCareJourneyQualityValues}
                    setSelectedValues={setSelectedCareJourneyQualityValues}
                    testId={`provider-claims-filters__multiselect--carejourney-scores-${props.tableType}`}
                    title="CareJourney Quality"
                  />
                </div>
              )}
              {props.showSpecialtyPredictionModel && (
                <div className="col-md-2">
                  <SpecialtyPredictionModelFilter
                    selectedSpecialtyPredictionModelFilter={selectedSpecialtyPredictionModelFilter}
                    setSelectedSpecialtyPredictionModelFilter={setSelectedSpecialtyPredictionModelFilter}
                    testId={`provider-claims-filters__select--specialty-prediction-model-${props.tableType}`}
                  />
                </div>
              )}
            </div>
            
            {/* Search section */}
            <ProviderSearchRow
              applyDisabled={applyDisabled()}
              buttonTestIdPrefix="providers-claims-filters__button"
              clearDisabled={clearDisabled()}
              onApply={() => props.handleProviderSearch(
                searchValue,
                selectedEffectivenessValues.map(x => x.key),
                selectedEfficiencyValues.map(x => x.key),
                selectedVolumeLabels.map(x => x.key),
                selectedLineOfBusiness,
                selectedLocationConfidenceValues.map(x => x.key),
                selectedMotiveAppropriatenessValues.map(x => x.key),
                selectedCareJourneyCostValues.map(x => x.key),
                selectedCareJourneyQualityValues.map(x => x.key),
                gainsPercent,
                selectedNewPatientClaimsValues.map(x => x.key),
                selectedProcedureValues.map(x => x.key.code),
                selectedConditionValues.map(x => x.key.code),
                selectedSpecialtyPredictionModelFilter,
                selectedConfidenceTierValues.map(x => x.key)
              )}
              onClear={() => onClearFiltersClick()}
              tableType={props.tableType}
            >
              <div className="col-md-3">
                <QESTextInput
                  testId={`provider-claims-filters__input--search-${props.tableType}`}
                  label="Provider Search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  value={searchValue || ""}
                />
              </div>
              <div className={codeSearchColumnClass}>
                <TooltipWrapper
                  position="top"
                  text={props.topProcedures?.length > 0 ? "" : "No procedures available"}
                  style={{ width: "100%" }}
                  triggerWidth="100%"
                >
                  <TopProcedureFilter
                    disabled={props.topProcedures?.length == 0}
                    topProcedures={props.topProcedures}
                    onSelectionChanged={(
                      selectedTopProcedures: DropdownItem<TopProcedure>[],) => {
                        setSelectedProcedureValues(selectedTopProcedures);
                      }
                    }
                    selectedTopProcedures={selectedProcedureValues}
                    setSelectedTopProcedures={setSelectedProcedureValues}
                    testId="pci-dashboard__filters--top-procedures"
                    title="Procedures Search"
                  />
                </TooltipWrapper>
              </div>
              <div className={codeSearchColumnClass}>
                  <TooltipWrapper
                      position="top"
                      text={props.topProcedures?.length > 0 ? "" : "No conditions available"}
                      style={{ width: "100%" }}
                      triggerWidth="100%"
                  >
                      <TopConditionFilter
                          disabled={props.topConditions?.length == 0}
                          topConditions={props.topConditions}
                          onSelectionChanged={(
                            selectedTopConditions: DropdownItem<TopCondition>[],) => {
                              setSelectedConditionValues(selectedTopConditions);
                            }
                          }
                          selectedTopConditions={selectedConditionValues}
                          setSelectedTopConditions={setSelectedConditionValues}
                          testId="pci-dashboard__filters--top-conditions"
                          title="Conditions Search"
                      />
                  </TooltipWrapper>
              </div>
              {props.showConfidenceTier && (
                <div className="col-md-2">
                  <ConfidenceTierFilter
                    selectedValues={selectedConfidenceTierValues}
                    setSelectedValues={setSelectedConfidenceTierValues}
                    testId={`provider-claims-filters__multiselect--confidence-tier-${props.tableType}`}
                    title="Confidence Level"
                  />
                </div>
              )}
            </ProviderSearchRow>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProviderClaimsFilters;
